<script lang="ts">
    import { onDestroy, onMount } from "svelte";
    import { slide } from "svelte/transition";
    import axios from "axios";
    import {
        UserData,
        UserEvent,
        UserMessage,
        UserMessageInterface,
    } from "./model";

    export let isMod = "";

    const WS_BASE_URL = "wss://hw-chat-server-ytlive.labvent.co";
    const HTTP_BASE_URL = "https://hw-chat-server-ytlive.labvent.co/n1/chat";

    // const WS_BASE_URL = "ws://localhost:9000";
    // const HTTP_BASE_URL = "http://localhost:9001/n1/chat";

    let ws: WebSocket;
    let connectionId = true;
    let liveUsers = 1;
    let userHandle: string = null;
    let message = "";
    let toggleDropdown = false;
    let wsLive = false;

    let chatArea: any;
    let hiddenScrollPointer: any;
    let chatScrollPaused = false;

    let userMessages: Array<UserMessage> = [];

    let chatStat = null;

    let checkWebsocketHealthAndInitiateObjectTimer: NodeJS.Timer;
    let getChatStatTimer: NodeJS.Timer;
    let chatScrollTimer: NodeJS.Timer;

    let isTabFocused = true;

    function processMessage(data: UserData) {
        switch (data.event) {
            case UserEvent.NEW_CONNECTION_ID:
                localStorage.setItem("connection_id", data.data?.id);
                connectionId = true;
                break;
            case UserEvent.USER_COUNT_UPDATE:
                liveUsers = data.data?.count;
                break;
            case UserEvent.MESSAGE_RECEIVED_EVENT:
                handleMessageReceived(data);
                break;
            case UserEvent.YOUTUBE_MESSAGE_RECEIVED_EVENT:
                handleYouTubeMessageReceived(data);
                break;
            case UserEvent.HANDLE_UPDATE:
                handleDisplayNameUpdate(data);
            case UserEvent.DELETE_CHAT:
                handleMessageDelete(data);
                break;
            default:
                // Default to ping, send pong
                ws.send(new UserData(UserEvent.PONG, {}).serialize());
                break;
        }
    }

    function handleMessageReceived(data: UserData) {
        const newUserMessage = UserMessage.parseUserMessage(data.data);
        userMessages.push(newUserMessage);

        userMessages = [...userMessages];

        handleChatScroll();
    }

    function handleYouTubeMessageReceived(data: UserData) {
        const newYouTubeMessage = UserMessage.parseUserMessage(data.data, true);

        if (!(newYouTubeMessage.handle || newYouTubeMessage.message)) {
            return;
        }

        userMessages.push(newYouTubeMessage);

        userMessages = [...userMessages];

        handleChatScroll();
    }

    function processString(messageContent: string | String) {
        if (isQuestion(messageContent)) {
            const newMessageContentBuffer = messageContent.split(" ");
            newMessageContentBuffer.shift();
            messageContent = newMessageContentBuffer.join(" ");
        }

        return messageContent;
    }

    function isQuestion(messageContent: string | String) {
        const questionTokens = ["!q", "!question", "!questions"];
        const token = messageContent.toLocaleLowerCase().split(" ").shift();

        return questionTokens.includes(token);
    }

    function handleDisplayNameUpdate(data: UserData) {
        const { current, previous }: { current: string; previous: string } =
            data.data.data;

        if (!(current || previous)) {
            return;
        }

        userMessages.forEach((userMessage) => {
            // Replace in place
            if (userMessage.handle === previous) {
                userMessage.handle = current;
            }
        });

        userMessages = [...userMessages];
    }

    function handleSetOrSend() {
        checkWebsocketHealthAndInitiateObject();

        if (!userHandle && message) {
            //  Check if existing user changed the display name
            const prevUser = localStorage.getItem("prev_user_handle");

            if (prevUser) {
                // Update personal message of the current user
                userMessages.forEach((userMessage) => {
                    // Replace in place
                    if (userMessage.handle === prevUser) {
                        userMessage.handle = message;
                    }
                });

                userMessages = [...userMessages];

                // Dispatch a display name update to server
                ws.send(
                    new UserData(UserEvent.HANDLE_UPDATE, {
                        current: message,
                        previous: prevUser,
                    }).serialize(),
                );
                localStorage.removeItem("prev_user_handle");
            }

            localStorage.setItem("user_handle", message);
            userHandle = message;
            message = "";
            toggleDropdown = false;
            return;
        }

        if (!message) {
            message = "";
            return;
        }

        if (wsLive) {
            const newMessage = new UserMessage(null, userHandle, message);
            userMessages.push(newMessage);

            ws.send(
                new UserData(
                    UserEvent.BROADCAST_MESSAGE,
                    newMessage.serialize(),
                ).serialize(),
            );

            userMessages = [...userMessages];

            message = "";
            handleChatScroll();
        }
    }

    function handleKeyPressOnMessage(event: KeyboardEvent) {
        if (event.key === "Enter") {
            event.preventDefault();
            handleSetOrSend();
        }
    }

    function handleChatScroll(focused = false) {
        if (!chatScrollPaused || focused) {
            chatScrollTimer = setTimeout(() => {
                chatArea.scrollTop = chatArea.scrollHeight;
            }, 100);
        }
    }

    function handleChatScrollPause() {
        chatScrollPaused = chatArea.scrollTop < chatArea.scrollHeight - 400;
    }

    function handleDisplayNameReset() {
        // Dispatch user handle update to server
        localStorage.setItem("prev_user_handle", userHandle);
        localStorage.removeItem("user_handle");
        userHandle = null;
    }

    function checkWebsocketHealthAndInitiateObject() {
        const faultyStates = [WebSocket.CLOSING, WebSocket.CLOSED];
        if (faultyStates.includes(ws?.readyState) || !wsLive) {
            ws = new WebSocket(WS_BASE_URL);

            ws.onmessage = (message) => {
                if (userHandle) {
                    processMessage(JSON.parse(message.data) as UserData);
                }
            };

            ws.onopen = () => {
                ws.send(new UserData(UserEvent.JOIN_CHAT, {}).serialize());
                wsLive = true;
            };

            ws.onclose = () => {
                wsLive = false;
            };
        }
    }

    async function fetchChatHistory() {
        try {
            const response =
                await axios.get<UserMessageInterface[]>(HTTP_BASE_URL);

            userMessages = response.data.map((messages) =>
                UserMessage.parseUserMessage(messages),
            );

            // Add command card
            // userMessages = [
            //     ...userMessages,
            //     new UserMessage(
            //         "",
            //         "HealthiWealthi™ Bot",
            //         `
            //         <p><strong>Commands:</strong></p>
            //         <ul>
            //             <li>Use any of the following command prefix if you have a question <em>!q, !question</em></li>
            //         </ul>
            //         <p><strong>Links:</strong></p>
            //         <ul>
            //             <li><strong><a data-fr-linked="true" href="https://www.healthiwealthi.net" target="_blank" rel="noopener noreferrer">https://www.healthiwealthi.net</a></strong></li>
            //             <li><strong><a data-fr-linked="true" href="https://healthiwealthi.io/signup" target="_blank" rel="noopener noreferrer">https://healthiwealthi.io/signup</a></strong></li>
            //         </ul>
            //         `
            //     ),
            // ];
        } catch (error) {
            console.error("error fetching chat history, refresh page");
        } finally {
            setTimeout(handleChatScroll, 2000);
        }
    }

    async function getChatStat() {
        const response = await axios.get(`${HTTP_BASE_URL}/stat`);
        chatStat = response.data;
    }

    async function handleDeleteChat(messageId: string) {
        if (!messageId) {
            return;
        }

        const deleteUrl = `${HTTP_BASE_URL}/${messageId}`;

        await axios.delete(deleteUrl);
    }

    function handleMessageDelete(data: UserData) {
        const { id } = data.data;
        userMessages = userMessages.filter(
            (userMessage) => userMessage.id !== id,
        );
    }

    function handleFocusChange() {
        isTabFocused = true;
        handleChatScroll(isTabFocused);
    }

    function handleBlur() {
        isTabFocused = false;
    }

    onMount(async () => {
        localStorage.removeItem("connection_id");

        userHandle = localStorage.getItem("user_handle");

        checkWebsocketHealthAndInitiateObject();

        if (userHandle) {
            await fetchChatHistory();
        }

        // Initialize websocket health check
        checkWebsocketHealthAndInitiateObjectTimer = setInterval(() => {
            checkWebsocketHealthAndInitiateObject();
        }, 10 * 1000);

        // Initialize chat stats
        getChatStatTimer = setInterval(() => {
            getChatStat();
        }, 5 * 1000);

        window.addEventListener("focus", handleFocusChange);
        window.addEventListener("blur", handleBlur);
    });

    onDestroy(() => {
        clearInterval(checkWebsocketHealthAndInitiateObjectTimer);
        clearInterval(getChatStatTimer);
        clearInterval(chatScrollTimer);

        window.removeEventListener("focus", handleFocusChange);
        window.removeEventListener("blur", handleBlur);

        ws.close();
    });
</script>

<section
    class="flex basis-[550px] 2xl:basis-[620px] relative h-[530px] border border-[#6322ae] overflow-hidden rounded-md"
>
    <div class="w-full absolute top-0 left-0 mt-2">
        <div class="flex items-center justify-between px-2 shadow-md mb-2 pb-2">
            <h3 class="text-md text-[#333333] font-semibold pl-3">
                <i class="bi bi-chat-dots-fill" /> Live Chat
                <!-- <small>
                    Live Users <i class="bi bi-people-fill" />
                    {liveUsers}</small
                > -->
            </h3>

            <div class="flex items-center">
                <small class="mr-4">
                    <i class="bi bi-chat-text" />
                    {!chatStat?.counter ? 0 : chatStat?.counter}
                </small>

                <div class="relative text-left">
                    <div>
                        <button
                            type="button"
                            class="inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:ring-offset-gray-100"
                            id="menu-button"
                            aria-expanded="true"
                            aria-haspopup="true"
                            on:click={() => (toggleDropdown = !toggleDropdown)}
                        >
                            <i class="bi bi-gear-fill" />
                            <!-- Heroicon name: mini/chevron-down -->
                            <svg
                                class="-mr-1 ml-2 h-5 w-5"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                                aria-hidden="true"
                            >
                                <path
                                    fill-rule="evenodd"
                                    d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
                                    clip-rule="evenodd"
                                />
                            </svg>
                        </button>
                    </div>

                    {#if toggleDropdown}
                        <div
                            transition:slide|local
                            class="absolute right-0 z-10 mt-2 w-56 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
                            role="menu"
                            aria-orientation="vertical"
                            aria-labelledby="menu-button"
                            tabindex="-1"
                        >
                            <div class="py-1" role="none">
                                <!-- Active: "bg-gray-100 text-gray-900", Not Active: "text-gray-700" -->
                                <a
                                    href="#"
                                    class="text-gray-700 block px-4 py-2 text-sm"
                                    role="menuitem"
                                    tabindex="-1"
                                    id="menu-item-0"
                                    on:click|preventDefault={handleDisplayNameReset}
                                    >Reset Display Name</a
                                >
                                <a
                                    href="#"
                                    class="text-gray-700 block px-4 py-2 text-sm"
                                    role="menuitem"
                                    tabindex="-1"
                                    id="menu-item-1"
                                    on:click={() =>
                                        window.open(
                                            "https://www.lifestylemedicine.ai/contact-us",
                                            "_blank",
                                        )}>Support</a
                                >
                            </div>
                        </div>
                    {/if}
                </div>
            </div>
        </div>

        {#if !userHandle}
            <div class="flex flex-row justify-center items-center mt-20">
                <div
                    class="block rounded-lg shadow-lg bg-white max-w-sm text-center"
                >
                    <div class="py-3 px-6 border-b border-gray-300">
                        Welcome!
                    </div>
                    <div class="p-6">
                        <h5 class="text-gray-900 text-xl font-medium mb-2">
                            Choose a display name
                        </h5>
                        <div class="flex justify-center">
                            <div class="mb-3 xl:w-96">
                                <input
                                    type="text"
                                    class="form-control block w-full px-3 py-1.5 text-base font-normal text-gray-700bg-white bg-clip-padding border border-solid border-gray-300 rounded transition ease-in-out m-0 focus:text-gray-700 focus:bg-white focus:border-blue-600 focus:outline-none"
                                    placeholder="Display Name"
                                    bind:value={message}
                                />
                            </div>
                        </div>
                        <button
                            type="button"
                            class=" inline-block px-6 py-2.5 bg-blue-600 text-white font-medium text-xs leading-tight uppercase rounded shadow-md hover:bg-blue-700 hover:shadow-lg focus:bg-blue-700 focus:shadow-lg focus:outline-none focus:ring-0 active:bg-blue-800 active:shadow-lg transition duration-150 ease-in-out"
                            on:click={() => {
                                handleSetOrSend();
                                fetchChatHistory();
                            }}>Set Display Name</button
                        >
                    </div>
                </div>
            </div>
        {/if}

        <div
            bind:this={chatArea}
            on:scroll={handleChatScrollPause}
            class="h-[400px] overflow-y-scroll px-3 pb-4"
        >
            <!-- === cards == -->
            {#each userMessages as userMessage}
                <div class="w-full mb-3">
                    <div class="bg-white w-full rounded-md py-2.5 px-2 gap-3">
                        <div class="flex justify-between w-full">
                            <h2 class="text-xs font-medium mb-1">
                                {#if userMessage.isYoutube}
                                    <i
                                        class="bi bi-youtube"
                                        style="color: red;"
                                    />
                                    <strong>@{userMessage.handle}</strong>
                                {:else}
                                    <strong>@{userMessage.handle}</strong>
                                {/if}
                            </h2>
                            <h2 class="text-xs font-medium mb-1">
                                {#if isMod}
                                    <button
                                        class="inline-block px-1 py-1 bg-red-600 text-white font-medium text-xs leading-tight uppercase rounded shadow-md hover:bg-red-700 hover:shadow-lg focus:bg-red-700 focus:shadow-lg focus:outline-none focus:ring-0 active:bg-red-800 active:shadow-lg transition duration-150 ease-in-out"
                                        on:click={() =>
                                            handleDeleteChat(userMessage.id)}
                                    >
                                        <i class="bi bi-trash3-fill" />
                                    </button>
                                {/if}
                                {#if isQuestion(userMessage.message)}
                                    <span class="text-pink-500"
                                        >Question <i
                                            class="bi bi-question-square-fill"
                                        /></span
                                    >
                                {/if}
                                <small class="text-xs italic">
                                    {userMessage.time}
                                </small>
                            </h2>
                        </div>
                        <hr />
                        <p
                            class="text-xs text-gray-800 font-medium mx-2 text-start"
                        >
                            {@html processString(userMessage.message).replace(
                                new RegExp(/@([^\s]+)/gm),
                                `<strong class="text-pink-500">@$1</strong>`,
                            )}
                        </p>
                    </div>
                </div>
            {/each}
            <div bind:this={hiddenScrollPointer} />
        </div>
    </div>
    {#if userHandle}
        <div
            class="absolute bottom-0 bg-gray-100 left-0 h-20 w-full px-4 z-50 flex justify-between gap-1 items-center border-t"
            style="z-index: auto;"
        >
            <textarea
                placeholder="Type here..."
                class="bg-white w-full py-2.5 px-3 text-sm text-gray-800 rounded outline-none focus:ring-1 focus:ring-[#6322ae] border-[#6322ae] border-2"
                style="resize:none;"
                rows="2"
                bind:value={message}
                on:keypress={handleKeyPressOnMessage}
            />
            <button
                type="submit"
                class="font-medium md:h-14 h-10 md:w-14 w-12 text-xs text-white rounded-full bg-[#6322ae] outline-none border-none flex justify-center items-center text-md"
                on:click={handleSetOrSend}
            >
                <i class="bi bi-send-fill" />
            </button>
        </div>
    {/if}
</section>

<style>
    main {
        text-align: center;
        padding: 1em;
        max-width: 240px;
        margin: 0 auto;
    }

    @media (min-width: 640px) {
        main {
            max-width: none;
        }
    }
</style>
